/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { globalRef, parseBoolean } from '@amazon/vinyl-util'
import { queryParam } from '@/util/queryParams'
import { type Auth, AuthStatus } from '@amzn/amp-playback'
import { LwaAuth } from '@/auth/LwaAuth'
import { getDeviceConfig } from '@/device/deviceConfig'
import { CblAuth } from '@/auth/CblAuth'
import { getCodeData } from '@/auth/createCodeData'
import { localSignInPersistence } from '@/core/signInPersistence'
import { displayPublicCodePrompt } from '@/auth/PublicCodePrompt'
import { MapAuth } from '@/auth/MapAuth'
import { AuthPortalAuth } from '@/auth/AuthPortalAuth'
import { type ObservableValue } from '@amazon/vinyl-observable'
import { type DeviceConfig } from '@/device/deviceConfig'

// Caddy fronts the AuthPortal dev server here (matches the LOCAL_HOSTS entry
// in `service/useLiveHost`); serving from this origin means cookie-session auth.
const AUTH_PORTAL_HOST = 'local.music.amazon.com'

// Explicit override: `?authPortal=1` forces the cookie-session flow regardless
// of host or device config. A missing param or `=0` defers to detection below.
function usesAuthPortalParam(): boolean {
    try {
        const raw = queryParam('authPortal')
        if (raw != null) return parseBoolean(raw)
    } catch {
        // No URL context (non-browser transform path) — flag off.
    }
    return false
}

function isAuthPortalHost(): boolean {
    return typeof location !== 'undefined' && location.hostname === AUTH_PORTAL_HOST
}

function isCookieAuthFlagEnabled(): boolean {
    return typeof window !== 'undefined' && window.amznMusic?.isCookieAuthEnabled === true
}

// First-class cookie-auth detection for the web browser. Gated to 'web' configs
// so a Vega ('map') or Smart TV ('cbl') device keeps its own controller —
// host/flag detection is a browser-only signal and must never shadow the
// device-config auth type below.
function usesCookieAuth(config: DeviceConfig): boolean {
    if (config.authType !== 'web') return false
    return isAuthPortalHost() || isCookieAuthFlagEnabled()
}

export const authRef = globalRef<Auth>(() => {
    const config = getDeviceConfig()
    if (usesAuthPortalParam() || usesCookieAuth(config)) {
        return new AuthPortalAuth()
    }
    switch (config.authType) {
        case 'map':
            return new MapAuth()
        case 'cbl':
            return new CblAuth({
                codeDataProvider: getCodeData,
                persistence: localSignInPersistence,
                displayCodeCallback: displayPublicCodePrompt,
            })
        case 'web':
            return new LwaAuth()
    }
})

export function getAuth(): Auth {
    return authRef.value
}

export function getAuthToken(): Promise<string> {
    return authRef.value.authTokenProvider()
}

/**
 * Vend the bearer token for a cross-origin socket service — the Hermes
 * party-queue WebSocket and the claw agent init/socket — that the first-party
 * session cookie can't reach, so it must authenticate with a token in BOTH auth
 * modes. Unlike the gql/DMLS paths (which drop the bearer under AuthPortal and
 * authenticate with the session cookie + CSRF triple instead), these services
 * live on a different origin than the page and always need the token: in
 * cookie-session mode it is the AuthPortal session's access token (the
 * `/config.json` boot token, refreshed via `/pandaToken`); in bearer mode it is
 * the LWA/MAP/CBL bearer. Returns undefined when signed out so the caller opens
 * the socket tokenless rather than throwing and failing every subscription.
 */
export async function getSocketAuthToken(): Promise<string | undefined> {
    const auth = authRef.value
    if (!auth.isSignedIn()) return undefined
    // A cookie session can be signed in without one (AuthPortal withholds it until
    // a profile is picked); open tokenless rather than fail every subscription.
    return auth.authTokenProvider().catch(() => undefined)
}

/**
 * A data provider for the current sign-in state.
 */
export const signedIn: () => ObservableValue<boolean> = () =>
    getAuth().status.map((v) => v === AuthStatus.SIGNED_IN)

/**
 * True only when the session is explicitly signed out. PENDING is NOT
 * signed-out — gating signed-out surfaces (sign-in button, anon banner) on
 * this keeps them hidden during the cold-start auth-unknown window, so a
 * signed-in reload never flashes a "Sign in" affordance before status settles.
 */
export const notSignedIn: () => ObservableValue<boolean> = () =>
    getAuth().status.map((v) => v === AuthStatus.SIGNED_OUT)

/**
 * True whenever the session is not confirmed signed-in — i.e. through PENDING
 * AND SIGNED_OUT. Unlike `notSignedIn` (SIGNED_OUT-only), this stays true during
 * the cold-start auth-unknown window, so it gates surfaces that must show until
 * sign-in is confirmed (e.g. the MAP waiting message, the public code prompt).
 */
export const notSignedInOrPending: () => ObservableValue<boolean> = () =>
    getAuth().status.map((v) => v !== AuthStatus.SIGNED_IN)

/** True once the sign-in state is known either way; false through PENDING. */
export const authSettled: () => ObservableValue<boolean> = () =>
    getAuth().status.map((v) => v !== AuthStatus.PENDING)

const anonSessionRef = globalRef<{ id: string | null }>(() => ({ id: null }))

/**
 * Creates or gets a unique id for this session.
 *
 * Must satisfy ^\d{3}-\d{7}-\d{7}$
 */
export function getAnonSessionId(): string {
    if (!anonSessionRef.value.id) {
        const part1 = Math.floor(Math.random() * 1e3)
            .toString()
            .padStart(3, '0')
        const part2 = Math.floor(Math.random() * 1e7)
            .toString()
            .padStart(7, '0')
        const part3 = Math.floor(Math.random() * 1e7)
            .toString()
            .padStart(7, '0')
        anonSessionRef.value.id = `${part1}-${part2}-${part3}`
    }
    return anonSessionRef.value.id
}
